import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import Link from 'next/link'
import { ArrowRight, Clock } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'

import { NAV_LINKS, SCHEDULE_OPTIONS, siteConfig } from '@/constants/site'

const START_LINKS: Array<NavLink> = [
    { label: "Enroll in a course", href: '/enroll' },
    { label: "Browse all courses", href: '/courses' },
    { label: "Ask an advisor", href: '/enroll#enroll-form' }
]

interface FooterColumnProps {
    title: string
    children: ReactNode
}

const FooterColumn: FC<FooterColumnProps> = ({ title, children }): ReactNode => {
    return (
        <div>
            <h2 className='font-mono text-xs font-medium tracking-wider text-background/50 uppercase'>
                {title}
            </h2>

            <ul className='mt-4 space-y-3 text-sm'>
                {children}
            </ul>
        </div>
    )
}

export const SiteFooter: FC = (): ReactNode => {
    const year = new Date().getFullYear()

    return (
        <footer className='relative overflow-hidden bg-foreground text-background'>
            <div
                aria-hidden='true'
                className='bg-dots pointer-events-none absolute inset-0 text-background/40 [mask-image:linear-gradient(to_bottom,black,transparent_75%)]'
            />

            <Container className='relative py-14 sm:py-16'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]'>
                    <div className='sm:col-span-2 lg:col-span-1'>
                        <Logo tone='inverted' />

                        <p className='mt-5 max-w-sm text-sm leading-relaxed text-background/70'>
                            {siteConfig.description}
                        </p>

                        <ButtonLink href='/enroll' variant='accent' size='sm' className='mt-6'>
                            Enroll now
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>
                    </div>

                    <FooterColumn title="Explore">
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-background/80 transition-colors hover:text-accent'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </FooterColumn>

                    <FooterColumn title="Start learning">
                        {START_LINKS.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-background/80 transition-colors hover:text-accent'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </FooterColumn>

                    <FooterColumn title="Class times">
                        {SCHEDULE_OPTIONS.map(option => (
                            <li key={option} className='flex items-center gap-2 text-background/80'>
                                <Clock className='size-3.5 text-accent' aria-hidden='true' />
                                {option}
                            </li>
                        ))}
                    </FooterColumn>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-background/15 pt-6 text-xs text-background/55 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{year}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p className='font-mono'>
                        Hands-on computer courses for every level.
                    </p>
                </div>
            </Container>
        </footer>
    )
}
